import type { Theme } from '@mui/material';

import goLogo from '../../../../assets/img/go-logo.svg';
import { goPalette, tsGoUnit } from '../tsGoThemeVars';

function getContainerStyles() {
	return {
		height: '100%',
		overflow: 'auto',
		padding: tsGoUnit.tsUnit,
		backgroundColor: goPalette.tsGoColor.grayLightest,
		boxSizing: 'border-box',
		maxWidth: '100vw',
	};
}

function getSideMenuFooterStyle() {
	return {
		display: 'flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		height: tsGoUnit.tsUnitTriple,
		zIndex: 1,
		backgroundColor: '#304249',
		borderTop: '#192226 1px solid',
		boxShadow: '0 -5px 10px rgba(25, 34, 38, 0.4)',
		overflow: 'hidden',
		whiteSpace: 'nowrap',
		borderRadius: 0,
	};
}

function getSideMenuHeaderStyle(ownerState, theme: Theme) {
	const backgroundImage = `url(${goLogo})`;
	const defaultStyles = {
		backgroundColor: '#222e33',
		height: tsGoUnit.tsUnitTriple,
		zIndex: 1,
		borderBottom: '#192226 1px solid',
		boxShadow: '0 5px 10px rgba(25, 34, 38, 0.4)',
		position: 'relative',
		backgroundImage,
		backgroundSize: '56px 26px',
		backgroundRepeat: 'no-repeat',
		backgroundPosition: 'center',
		cursor: 'pointer',
		borderRadius: 0,
		boxSizing: 'border-box',
		'--collapseButtonTransition': 'opacity 0.1s, background-color 0.25s, right 0.2s, left 0.2s',
	};
	if (ownerState['ts-variant'] === 'sideMenuHeaderPaper') {
		return {
			...defaultStyles,
		};
	} else if (ownerState['ts-variant'] === 'sideMenuHeaderCollapsed') {
		return {
			...defaultStyles,
			backgroundSize: '46px 20px',
			transition: 'width 0.3s linear',
			animation: 'purple-shadow-glow-animation 0.7s ease-in-out',

			[theme.breakpoints.up('md')]: {
				'> div': {
					width: 0,
					left: '100%',
					borderRadius: '0 15px 15px 0',
					backgroundColor: '#222e33',
					opacity: 1,
				},

				'&:hover': {
					'> div': {
						width: '15px',
						transition: 'width 0.1s linear',

						'> svg': {
							opacity: 1,
						},
					},
				},
			},
		};
	}
	return { ...defaultStyles };
}

function getSideMenuSwitcherStyle(ownerState, theme) {
	const defaultStyles = {
		opacity: 1,
		display: 'none',
		left: '60px',
		bottom: 0,
		backgroundColor: '#192226',
		height: '100%',
		overflow: 'hidden',
		position: 'absolute',
		top: 0,
		paddingTop: 0,
		zIndex: -1,
		transition: 'transform 0.3s ease-in-out',
		width: '300px',

		[theme.breakpoints.down('md')]: {
			zIndex: 1,
			left: 0,
			width: '100%',
		},
	};
	if (ownerState.className === 'open') {
		return {
			...defaultStyles,
			transform: 'translateX(0)',
			display: 'flex',
			flexDirection: 'column',

			'& > .side-menu-team-switcher__menu': {
				flexGrow: 1,
				overflowY: 'auto',
			},
		};
	} else if (ownerState.className === 'closed') {
		return {
			...defaultStyles,
			transform: 'translateX(-100%)',
		};
	}
	return { ...defaultStyles };
}

function getSideMenuMyRequestStyle(ownerState, theme) {
	const defaultStyles = {
		opacity: 1,
		display: 'none',
		left: '60px',
		bottom: 0,
		backgroundColor: '#192226',
		height: '100%',
		overflow: 'hidden',
		position: 'absolute',
		top: 0,
		paddingTop: 0,
		zIndex: -1,
		transition: 'transform 0.3s ease-in-out',
		width: '300px',
		overflowY: 'auto',
		borderRadius: 0,

		[theme.breakpoints.down('md')]: {
			zIndex: 1,
			left: 0,
			width: '100%',
		},
	};
	if (ownerState.className === 'open') {
		return {
			...defaultStyles,
			transform: 'translateX(0)',
			display: 'block',
		};
	} else if (ownerState.className === 'closed') {
		return {
			...defaultStyles,
			transform: 'translateX(-100%)',
		};
	}
	return { ...defaultStyles };
}

function getSideMenuPanelStyle() {
	return {
		backgroundColor: '#222e33',
		paddingLeft: 0,
		paddingRight: 0,
		paddingTop: 0,
		cursor: 'pointer',
		whiteSpace: 'nowrap',
		flex: 1,
		position: 'relative',
		borderRadius: 0,
		scrollbarWidth: 'thin',

		'& > ul': {
			padding: 0,
		},
	};
}

function getModalStyles() {
	return {
		overflow: 'auto',
		padding: '20px',
		backgroundColor: goPalette.tsGoColor.white,
	};
}

export const muiPaperOverrides = {
	styleOverrides: {
		root: ({ ownerState, theme }) => {
			return {
				variants: [
					{
						props: {
							'ts-variant': 'container',
						},
						style: getContainerStyles(),
					},
					{
						props: {
							'ts-variant': 'modal',
						},
						style: getModalStyles(),
					},
					{
						props: {
							'ts-variant': 'sideMenuFooterPaper',
						},
						style: getSideMenuFooterStyle(),
					},
					{
						props: ({ ownerState }) =>
							ownerState['ts-variant'] === 'sideMenuHeaderPaper' ||
							ownerState['ts-variant'] === 'sideMenuHeaderCollapsed',
						style: getSideMenuHeaderStyle(ownerState, theme),
					},
					{
						props: {
							'ts-variant': 'sideMenuTeamSwitcher',
						},
						style: getSideMenuSwitcherStyle(ownerState, theme),
					},
					{
						props: {
							'ts-variant': 'sideMenuMyRequest',
						},
						style: getSideMenuMyRequestStyle(ownerState, theme),
					},
					{
						props: {
							'ts-variant': 'sideMenuPanel',
						},
						style: getSideMenuPanelStyle(),
					},
				],
			};
		},
	},
};
